import {
  useFlightPlanQuery,
  useCancelFlightPlanMutation,
} from "@/api/v1/flight-plan-api";
import { useParams, useNavigate } from "@tanstack/react-router";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  Calendar,
  Clock,
  MapPin,
  Plane,
  User,
  FileText,
  Shield,
  Download,
  Ban,
  Info,
  AlertTriangle,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { format } from "date-fns";
import { Map, MapRef } from "react-map-gl/maplibre";
import { useStore } from "@/store/store";
import { BreadcrumbNav } from "@/components/ui/breadcrumb-nav";
import { FlightPlanRequest } from "@/api/v1/flight-plan-types";
import {
  GeofencePolygonGeometry,
  PolygonGeometry,
} from "@/api/v1/geofence-types";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import { useRef, useEffect, useMemo, useState } from "react";
import { calculateBounds } from "@/lib/geo-utils";
import { FlightPlanItem } from "@/components/map/flight-plan-layer";
import { FlightRestrictionsLayer } from "@/components/map/flight-restrictions-layer";

export const FlightPlanDetailRoute = () => {
  // STEP 1: Call all hooks FIRST, before any early returns
  // Get the ID from the route params
  const { flightPlanId } = useParams({ strict: false }) as {
    flightPlanId: string;
  };
  const navigate = useNavigate();
  const mapRef = useRef<MapRef>(null);

  const {
    data: flightPlan,
    isLoading,
    error,
  } = useFlightPlanQuery(flightPlanId);
  const cancelMutation = useCancelFlightPlanMutation();
  const mapStyleUrl = useStore((state) => state.mapStyleUrl);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [showFlightRestrictions, setShowFlightRestrictions] = useState(true);

  const getPolygonCoordinates = (
    plan: FlightPlanRequest | undefined
  ): GeofencePolygonGeometry | null => {
    if (plan?.polygon_zone) {
      return {
        type: "polygon",
        coordinates: plan.polygon_zone as PolygonGeometry,
      };
    }
    return null;
  };

  const polygonGeometry = getPolygonCoordinates(flightPlan);

  const initialViewState = useMemo(() => {
    if (
      !polygonGeometry ||
      !(polygonGeometry as GeofencePolygonGeometry).coordinates
    ) {
      return {
        longitude: -0.1276,
        latitude: 51.5074,
        zoom: 13,
      };
    }

    const bounds = calculateBounds(
      (polygonGeometry as GeofencePolygonGeometry).coordinates
    );
    if (!bounds) {
      return {
        longitude: -0.1276,
        latitude: 51.5074,
        zoom: 13,
      };
    }

    const [minLng, minLat, maxLng, maxLat] = bounds;
    const centerLng = (minLng + maxLng) / 2;
    const centerLat = (minLat + maxLat) / 2;

    const lngDiff = maxLng - minLng;
    const latDiff = maxLat - minLat;
    const maxDiff = Math.max(lngDiff, latDiff);

    let zoom = 14;
    if (maxDiff > 0.1) zoom = 10;
    else if (maxDiff > 0.05) zoom = 11;
    else if (maxDiff > 0.02) zoom = 12;
    else if (maxDiff > 0.01) zoom = 13;
    else zoom = 14;

    return {
      longitude: centerLng,
      latitude: centerLat,
      zoom,
    };
  }, [polygonGeometry]);

  // STEP 3: All useEffect hooks must be called before any returns
  useEffect(() => {
    if (!mapRef.current || !polygonGeometry) return;

    const bounds = calculateBounds(
      (polygonGeometry as GeofencePolygonGeometry).coordinates
    );
    if (bounds) {
      const timer = setTimeout(() => {
        if (mapRef.current) {
          mapRef.current.fitBounds(
            [
              [bounds[0], bounds[1]], // [minLng, minLat]
              [bounds[2], bounds[3]], // [maxLng, maxLat]
            ],
            {
              padding: 50,
              duration: 1000,
            }
          );
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [polygonGeometry]);
  
  if (isLoading) {
    return (
      <div className="container mx-auto p-8 space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10 rounded-full" />
          <div className="space-y-2">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-32" />
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Skeleton className="h-[400px] lg:col-span-2 rounded-xl" />
          <Skeleton className="h-[400px] rounded-xl" />
        </div>
      </div>
    );
  }

  if (error || !flightPlan) {
    return (
      <div className="container mx-auto p-8 text-center">
        <h2 className="text-2xl font-bold mb-4">Flight Plan Not Found</h2>
        <p className="text-muted-foreground mb-6">
          The flight plan you are looking for could not be found or you do not
          have permission to view it.
        </p>
        <Button onClick={() => navigate({ to: "/flight-plans" })}>
          Return to List
        </Button>
      </div>
    );
  }

  const handleCancel = async () => {
    try {
      await cancelMutation.mutateAsync(flightPlanId);
      toast.success("Flight plan cancelled successfully");
    } catch (error) {
      console.error("Failed to cancel flight plan:", error);
      toast.error("Failed to cancel flight plan");
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "approved":
        return "bg-green-500/10 text-green-500 border-green-500/20";
      case "rejected":
        return "bg-red-500/10 text-red-500 border-red-500/20";
      case "submitted":
      case "awaiting_approval":
      case "validating":
        return "bg-blue-500/10 text-blue-500 border-blue-500/20";
      case "no_conflicts_found":
        return "bg-slate-500/10 text-slate-500 border-slate-500/20";
      case "requires_external_authorisation":
      case "pending_external_authorisation":
        return "bg-amber-500/10 text-amber-500 border-amber-500/20";
      case "requires_manual_review":
        return "bg-yellow-500/10 text-yellow-500 border-yellow-500/20";
      case "cancelled":
      case "draft":
      case "expired":
        return "bg-gray-500/10 text-gray-500 border-gray-500/20";
      default:
        return "bg-gray-500/10 text-gray-500 border-gray-500/20";
    }
  };

  const isCancellable = !["cancelled", "rejected", "expired", "no_conflicts_found", "requires_external_authorisation", "pending_external_authorisation"].includes(
    flightPlan.status
  );

  const getStatusDisplay = (status: string) => {
    if (status === "pending_external_authorisation" || status === "requires_external_authorisation") {
      return "PENDING EXTERNAL AUTHORISATION";
    }
    return status.replace(/_/g, " ").toUpperCase();
  };

  return (
    <main>
      <div className="container mx-auto p-4 sm:p-6 lg:p-8 space-y-6">
        <BreadcrumbNav
          items={[
            { label: "Home", href: "/" },
            { label: "Flight Plans", href: "/flight-plans" },
            { label: flightPlan.reference_number || "Details" },
          ]}
          className="mb-6"
        />

        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="icon"
              onClick={() => navigate({ to: "/flight-plans" })}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold">
                  {flightPlan.reference_number || "Flight Plan"}
                </h1>
                <Badge
                  variant="outline"
                  className={getStatusColor(flightPlan.status)}
                >
                  {getStatusDisplay(flightPlan.status)}
                </Badge>
              </div>
              <p className="text-muted-foreground flex items-center gap-2 mt-1">
                <Calendar className="h-3 w-3" />
                Created on {format(new Date(flightPlan.created_at), "PPP")}
              </p>
            </div>
          </div>

          <div className="flex gap-2">
            {isCancellable && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="destructive"
                    disabled={cancelMutation.isPending}
                  >
                    <Ban className="h-4 w-4 mr-2" />
                    Cancel Plan
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      Are you absolutely sure?
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      This action cannot be undone. This will permanently cancel
                      your flight plan request.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Keep Plan</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleCancel}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      Yes, Cancel Plan
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            {flightPlan.status === "approved" && (
              <Button variant="outline">
                <Download className="h-4 w-4 mr-2" />
                Download Approval
              </Button>
            )}
          </div>
        </div>

        {/* Status Alerts */}
        {flightPlan.status === "no_conflicts_found" && (
          <Alert variant="default" className="border-slate-500/50 bg-slate-500/10">
            <Info className="h-4 w-4 text-slate-500" />
            <AlertTitle className="text-slate-500 font-bold">No Conflicts Found</AlertTitle>
            <AlertDescription className="text-slate-500/90">
              Your flight plan does not appear to intersect any restricted airspace in our records. 
              <strong> Please note:</strong> this is informational only and does not constitute official authorisation. 
              Always verify with relevant authorities before flying.
            </AlertDescription>
          </Alert>
        )}

        {(flightPlan.status === "requires_external_authorisation" || flightPlan.status === "pending_external_authorisation") && (
          <Alert variant="default" className="border-amber-500/50 bg-amber-500/10">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            <AlertTitle className="text-amber-500 font-bold">Pending External Authorisation</AlertTitle>
            <AlertDescription className="text-amber-500/90">
              <div className="space-y-2">
                <p>
                  {typeof flightPlan.validation_errors?.reason === 'string'
                    ? flightPlan.validation_errors.reason
                    : "Your flight plan intersects restricted airspace. The responsible authority is not yet available on our platform - you must contact them directly to obtain official authorisation before flying."}
                </p>
                {typeof flightPlan.validation_errors?.unmapped_zone_count === 'number' && (
                  <p className="text-xs font-medium bg-amber-500/20 w-fit px-2 py-0.5 rounded">
                    Intersects {flightPlan.validation_errors!.unmapped_zone_count as number} zone(s) requiring external approval.
                  </p>
                )}
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Manual Review Alert */}
        {flightPlan.status === "requires_manual_review" && (
          <Alert variant="default" className="border-yellow-500/50 bg-yellow-500/10">
            <Info className="h-4 w-4 text-yellow-500" />
            <AlertTitle className="text-yellow-500">Manual Review Required</AlertTitle>
            <AlertDescription className="text-yellow-500/90">
              This flight plan requires manual review. You will need to contact the relevant airspace operators directly to obtain approval for your flight. Please reach out to the operators responsible for the airspace zones that overlap with your planned route.
            </AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content - Map & Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Map Card */}
            <Card className="overflow-hidden">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="h-5 w-5" />
                    Flight Area
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Shield className="h-4 w-4 text-muted-foreground" />
                    <Label htmlFor="show-restrictions-detail" className="text-sm cursor-pointer">
                      Show Restrictions
                    </Label>
                    <Switch
                      id="show-restrictions-detail"
                      checked={showFlightRestrictions}
                      onCheckedChange={setShowFlightRestrictions}
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="h-[400px] relative w-full">
                  <Map
                    ref={mapRef}
                    id="flight-plan-detail-map"
                    mapStyle={mapStyleUrl}
                    initialViewState={initialViewState}
                    style={{ width: "100%", height: "100%" }}
                    mapLib={import("maplibre-gl")}
                    attributionControl={false}
                    onLoad={() => setMapLoaded(true)}
                  >
                    {mapLoaded && polygonGeometry && (
                      <FlightPlanItem
                        key={flightPlan.id}
                        flightPlan={flightPlan}
                      />
                    )}

                    {showFlightRestrictions && (
                      <FlightRestrictionsLayer show={showFlightRestrictions} />
                    )}
                  </Map>
                </div>
              </CardContent>
            </Card>

            {/* Flight Details */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Plane className="h-5 w-5" />
                  Flight Details
                </CardTitle>
              </CardHeader>
              <CardContent className="grid sm:grid-cols-2 gap-6">
                <div className="space-y-1">
                  <span className="text-sm text-muted-foreground">Purpose</span>
                  <p className="font-medium">{flightPlan.purpose}</p>
                </div>
                <div className="space-y-1">
                  <span className="text-sm text-muted-foreground">
                    Number of Aircraft
                  </span>
                  <p className="font-medium">{flightPlan.number_aircraft}</p>
                </div>
                <div className="space-y-1">
                  <span className="text-sm text-muted-foreground">
                    Altitude Range
                  </span>
                  <p className="font-medium">
                    {flightPlan.min_altitude_agl_ft}ft -{" "}
                    {flightPlan.max_altitude_agl_ft}ft AGL
                  </p>
                </div>
                <div className="space-y-1">
                  <span className="text-sm text-muted-foreground">
                    Takeoff Location
                  </span>
                  <div className="flex items-center gap-1">
                    <MapPin className="h-3 w-3 text-muted-foreground" />
                    <p className="font-medium">
                      {flightPlan.takeoff_location || "Not specified"}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Additional Info */}
            {flightPlan.additional_information && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Additional Information
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="whitespace-pre-wrap text-sm">
                    {flightPlan.additional_information}
                  </p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar - Schedule & Contact */}
          <div className="space-y-6">
            {/* Schedule Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Schedule
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center border-b pb-3">
                  <span className="text-sm text-muted-foreground">Starts</span>
                  <div className="text-right">
                    <p className="font-medium">
                      {format(new Date(flightPlan.starts_at), "MMM d, yyyy")}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {format(new Date(flightPlan.starts_at), "HH:mm")}
                    </p>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Ends</span>
                  <div className="text-right">
                    <p className="font-medium">
                      {format(new Date(flightPlan.ends_at), "MMM d, yyyy")}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {format(new Date(flightPlan.ends_at), "HH:mm")}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Operator Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Operator
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-1">
                  <span className="text-xs font-medium uppercase text-muted-foreground">
                    Operator ID
                  </span>
                  <p className="font-mono bg-muted p-1 rounded text-sm">
                    {flightPlan.operator_id || "N/A"}
                  </p>
                </div>
                <div className="space-y-1">
                  <span className="text-xs font-medium uppercase text-muted-foreground">
                    Contact Name
                  </span>
                  <p>{flightPlan.operator_contact_name || "N/A"}</p>
                </div>
                <div className="space-y-1">
                  <span className="text-xs font-medium uppercase text-muted-foreground">
                    Contact Info
                  </span>
                  <p className="text-sm">{flightPlan.operator_contact_email}</p>
                  <p className="text-sm">{flightPlan.operator_contact_phone}</p>
                </div>
              </CardContent>
            </Card>

            {/* Pilot Card */}
            {(flightPlan.flyer_id || flightPlan.pilot_contact_name) && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    Pilot
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {flightPlan.flyer_id && (
                    <div className="space-y-1">
                      <span className="text-xs font-medium uppercase text-muted-foreground">
                        Flyer ID
                      </span>
                      <p className="font-mono bg-muted p-1 rounded text-sm">
                        {flightPlan.flyer_id}
                      </p>
                    </div>
                  )}
                  {flightPlan.pilot_contact_name && (
                    <div className="space-y-1">
                      <span className="text-xs font-medium uppercase text-muted-foreground">
                        Pilot Name
                      </span>
                      <p>{flightPlan.pilot_contact_name}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </main>
  );
};
